import React, { useEffect, useState } from "react";
import {
  useNavigate
} from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  DollarSign,
  Save,
  Layers
} from "lucide-react";
import * as ownerApi from "../../api/payroll/salaryStructureApi";
import * as portalApi from "../../api/payroll/salaryStructurePortalApi";
import * as compOwnerApi from "../../api/payroll/salaryComponentApi";
import * as compPortalApi from "../../api/payroll/salaryComponentPortalApi";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Input,
  Select,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const http = axios.create({ baseURL: API, withCredentials: true });

export default function SalaryStructureForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const compApi = isPortal ? compPortalApi : compOwnerApi;
  const basePath = isPortal ? "/staff-panel/salary-structures" : "/salary-structures";
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const toast = useToast();

  // Load roles + salary components
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Backend: app.use("/roles", ...) → GET /roles/all
        const rolesRes = await http.get("/roles/all");
        const roleData = rolesRes.data;
        const roleList = Array.isArray(roleData)
          ? roleData
          : roleData.data || roleData.roles || [];
        if (!cancelled) setRoles(roleList);

        const res = await compApi.getAllSalaryComponents();
        const list = (Array.isArray(res) ? res : res?.data || []).filter(
          (c) => c.active !== false
        );
        if (!cancelled) {
          setComponents(
            list.map((c) => ({
              salaryComponentId: c._id,
              value: c.defaultValue ?? 0,
              _name: c.name,
              _type: c.type,
              _calc: c.calculationType,
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.message || "Failed to load roles/components");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  // Load staff when role filter changes (optional roleId query supported by backend)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingStaff(true);
      try {
        // Backend: app.use("/staff", ...) → GET /staff/all?roleId=
        const params = roleId ? { roleId } : {};
        const res = await http.get("/staff/all", { params });
        const data = res.data;
        const list = Array.isArray(data) ? data : data.data || data.staff || [];
        const active = list.filter((s) => s.active !== false);
        if (!cancelled) {
          setStaffList(active);
          setStaffId("");
        }
      } catch (err) {
        if (!cancelled) {
          setStaffList([]);
          toast.error(err.response?.data?.message || "Failed to load staff");
        }
      } finally {
        if (!cancelled) setLoadingStaff(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roleId, isPortal]);

  const handleValueChange = (idx, value) => {
    setComponents((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], value: Number(value) };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!staffId || !effectiveFrom) {
      toast.error("Select staff and effective date");
      return;
    }
    if (components.length === 0) {
      toast.error("Add salary components first");
      return;
    }
    setLoading(true);
    try {
      await api.createSalaryStructure({
        staffId,
        effectiveFrom,
        components: components.map(({ salaryComponentId, value }) => ({
          salaryComponentId,
          value: Number(value) || 0,
        })),
      });
      toast.success("Salary structure created");
      setTimeout(() => navigate(basePath), 800);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const roleOptions = [
    { label: "All roles", value: "" },
    ...roles.map((r) => ({ label: r.name || r.roleName || r._id, value: r._id })),
  ];

  const staffOptions = staffList.map((s) => ({
    label: `${s.name}${s.staffCode ? ` (${s.staffCode})` : ""}${
      s.designation ? ` — ${s.designation}` : ""
    }`,
    value: s._id,
  }));

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      {/* Navigation & Header */}
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
          onClick={() => navigate(basePath)}
          className="mb-2 -ml-2"
        >
          Back to Salary Structures
        </Button>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <DollarSign className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          New Salary Structure
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          Assign a component-wise pay structure to a staff member
        </p>
      </div>

      
      

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader className="bg-neutral-50/50">
            <CardTitle>Staff & Effective Date</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Filter by role"
                options={roleOptions}
                value={roleId}
                onChange={setRoleId}
              />

              <div>
                <Select
                  label="Staff"
                  options={
                    staffOptions.length > 0
                      ? staffOptions
                      : [{ label: loadingStaff ? "Loading staff…" : "Select staff", value: "" }]
                  }
                  value={staffId}
                  onChange={setStaffId}
                  disabled={loadingStaff}
                  placeholder={loadingStaff ? "Loading staff…" : "Select staff"}
                />
                {!loadingStaff && staffList.length === 0 && (
                  <p className="mt-1.5 text-sm text-warning-700">
                    No active staff found{roleId ? " for this role" : ""}.
                  </p>
                )}
              </div>
            </div>

            <Input
              type="date"
              label="Effective From"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              required
            />
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader className="flex flex-row items-center gap-1.5 bg-neutral-50/50">
            <Layers className="h-4 w-4 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
            <CardTitle>Components</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {components.length === 0 ? (
              <EmptyState
                icon={Layers}
                title="No active salary components"
                description="Create some under Salary Components first."
              />
            ) : (
              <div className="divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden rounded border border-neutral-200 dark:border-neutral-800">
                {components.map((c, idx) => (
                  <div
                    key={c.salaryComponentId}
                    className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">{c._name}</span>
                      <Badge variant={c._type === "EARNING" ? "success" : "danger"}>
                        {c._type}
                      </Badge>
                      <Badge variant="default" className="font-mono">
                        {c._calc === "PERCENTAGE_OF_BASIC" ? "% of Basic" : "Flat"}
                      </Badge>
                    </div>
                    <div className="w-full sm:w-32">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={c.value}
                        onChange={(e) => handleValueChange(idx, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-4 flex gap-3">
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            disabled={components.length === 0}
            leftIcon={<Save className="h-4 w-4" />}
          >
            Create
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate(basePath)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}